// Refactor 02/2026 — extrahiert aus AppointmentsPage.js (Phase 5, Iter 333).
// Verkapselt:
//   - handleManualBilling (große Confirm + alle offenen Termine abrechnen)
//   - handleMoveToSlot    (Termin zu neuem Slot verschieben, mit Kunden-
//                          Konflikt-Preflight)

import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { runManualBillingApi, fetchBillingRunStatusApi } from '@/services/appointmentsApi';
import { runCustomerConflictCheck } from '@/utils/customerConflictCheck';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useBillingActions = ({
  // Manual Billing
  billingInProgress,
  setBillingInProgress,
  // Move
  movingAppointment,
  setMovingAppointment,
  services,
  currentDate,
  customers,
  fetchAppointments,
}) => {
  const navigate = useNavigate();
  // Direktlink zum Abrechnungs-Log (Einstellungen → Automatische Abrechnung)
  const openBillingLog = { label: '📋 Log öffnen', onClick: () => navigate('/settings/advanced?tab=billing-auto') };

  const handleManualBilling = async () => {
    if (billingInProgress) {
      toast.warning('⏳ Abrechnung läuft bereits...');
      return;
    }
    const confirmBilling = window.confirm(
      '⚠️ WICHTIG: Diese Aktion rechnet alle offenen Termine ab!\n\n' +
        '• Heute + alle vergangenen offenen Termine werden abgerechnet\n' +
        '• Punkte/Abo-Kontingent wird automatisch abgezogen\n' +
        '• Kunden mit "Ende Monat" werden ÜBERSPRUNGEN — sie rechnet die Monats-Automatik ab\n\n' +
        'Möchten Sie fortfahren?'
    );
    if (!confirmBilling) return;

    setBillingInProgress(true);
    try {
      const data = await runManualBillingApi();
      if (data.success && data.background && data.run_id) {
        // Läuft im Hintergrund (504-Fix) → Status pollen bis fertig
        toast.info(
          `⏳ Abrechnung für ${data.total} Termine gestartet — läuft im Hintergrund...`,
          { duration: 6000 }
        );
        let finished = false;
        for (let i = 0; i < 150 && !finished; i++) {
          await new Promise((r) => setTimeout(r, 4000));
          try {
            const run = await fetchBillingRunStatusApi(data.run_id);
            if (run.status === 'done') {
              finished = true;
              toast.success(
                `✅ Abrechnung abgeschlossen!\n${run.processed}/${run.total} Termine verarbeitet, ${run.invoices_created} Rechnungen erstellt`,
                { duration: 8000 }
              );
              if (run.errors && run.errors.length > 0) {
                toast.warning(
                  `⚠️ ${run.errors.length} Fehler aufgetreten. Bitte Log prüfen.`,
                  { duration: 12000, action: openBillingLog }
                );
              }
            } else if (run.status === 'failed') {
              finished = true;
              toast.error(`❌ Abrechnung fehlgeschlagen: ${run.error || 'Unbekannter Fehler'}`);
            }
          } catch (pollErr) {
            console.error('Polling-Fehler:', pollErr);
          }
        }
        if (!finished) {
          toast.info('ℹ️ Abrechnung läuft noch — Ergebnis später im Abrechnungs-Log sichtbar.', { action: openBillingLog });
        }
        await fetchAppointments();
      } else if (data.success) {
        toast.success(
          `✅ Abrechnung abgeschlossen!\n${data.processed}/${data.total} Termine verarbeitet`,
          { duration: 5000 }
        );
        if (data.errors && data.errors.length > 0) {
          toast.warning(
            `⚠️ ${data.errors.length} Fehler aufgetreten. Bitte Log prüfen.`,
            { duration: 12000, action: openBillingLog }
          );
        }
        await fetchAppointments();
      } else {
        toast.error(`❌ Abrechnung fehlgeschlagen: ${data.error}`);
      }
    } catch (error) {
      console.error('Fehler bei manueller Abrechnung:', error);
      toast.error(
        `❌ Fehler: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setBillingInProgress(false);
    }
  };

  const handleMoveToSlot = async (trainer, timeSlot) => {
    if (!movingAppointment) return;

    try {
      // Service-Dauer ermitteln (fallback auf Termin-Dauer)
      const service = services.find((s) => s.id === movingAppointment.service_id);
      const duration = service
        ? service.duration_minutes
        : (new Date(movingAppointment.end_time) -
            new Date(movingAppointment.start_time)) /
          (1000 * 60);

      const [hours, minutes] = timeSlot.split(':').map(Number);
      const startTime = new Date(currentDate);
      startTime.setHours(hours, minutes, 0, 0);
      const endTime = new Date(startTime);
      endTime.setMinutes(endTime.getMinutes() + duration);

      const updateData = {
        trainer_id: trainer.trainer.id,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
      };

      // Preflight: Kunden-Konflikt (Vortag/selber Tag/Folgetag)?
      const movingCustomerId =
        movingAppointment.only_customer_id ||
        movingAppointment.customer_id ||
        (movingAppointment.customer_ids && movingAppointment.customer_ids[0]);
      const proceedMove = await runCustomerConflictCheck(
        movingCustomerId,
        startTime.toISOString(),
        movingAppointment.id,
        customers
      );
      if (!proceedMove) return;

      // 08/2026: Nur EIN Teilnehmer wird verschoben (Gruppen-Split)
      if (movingAppointment.only_customer_id) {
        await axios.post(
          `${API}/appointments/${movingAppointment.id}/move-participant`,
          {
            customer_id: movingAppointment.only_customer_id,
            target_trainer_id: trainer.trainer.id,
            target_start_time: startTime.toISOString(),
            target_end_time: endTime.toISOString(),
          }
        );
        toast.success(
          `${movingAppointment.only_customer_name || 'Teilnehmer'} verschoben — die übrigen Teilnehmer bleiben auf dem alten Termin`
        );
        setMovingAppointment(null);
        fetchAppointments();
        return;
      }

      await axios.put(
        `${API}/appointments/${movingAppointment.id}`,
        updateData
      );
      toast.success('Termin verschoben!');
      setMovingAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Verschieben');
    }
  };

  return {
    handleManualBilling,
    handleMoveToSlot,
  };
};

export default useBillingActions;
